import { SHIPMENT_ORDER_CSV_IMPORT } from '@/constants/common/message.constants'

import { type LogiFieldValue } from './logi-fields'
import {
  type CellOverrides,
  isErrorRow,
  type PreviewDataRow,
  type PreviewResult
} from './preview'

/**
 * 事前チェック API（取り込まずに BE の検証だけを走らせる）が返した 1 件の問題。
 * - rowNo: 確認画面の行番号（実効データ行の 1 始まり）。行に紐づかない問題は null
 * - field: 問題のある項目。項目を特定できない問題（明細ID の重複・運行計画の不整合等）は null
 */
export type ServerIssue = {
  rowNo: number | null
  field: LogiFieldValue | null
  message: string
}

/** 事前チェック API が返した「保存時に地点を自動作成する」予告。エラーではない。 */
export type SiteAutoCreate = {
  rowNo: number
  field: 'originSiteId' | 'destSiteId'
  code: string
}

/** 事前チェックの結果と、そのチェックに使った修正値（チェック後に変わったセルを見分けるため）。 */
export type ServerCheck = {
  issues: ServerIssue[]
  siteAutoCreates: SiteAutoCreate[]
  checkedOverrides: CellOverrides
}

export type PreviewWithServerCheck = PreviewResult & {
  /** 行に紐づかない問題（ファイル全体の問題）。 */
  globalIssues: string[]
  siteAutoCreates: SiteAutoCreate[]
}

const isCellUnchanged = (
  current: CellOverrides,
  checked: CellOverrides,
  rowNo: number,
  field: LogiFieldValue
): boolean => current[rowNo]?.[field] === checked[rowNo]?.[field]

const isRowUnchanged = (
  current: CellOverrides,
  checked: CellOverrides,
  rowNo: number
): boolean => {
  const a = current[rowNo] ?? {}
  const b = checked[rowNo] ?? {}
  const keys = new Set([...Object.keys(a), ...Object.keys(b)])
  for (const key of keys) {
    if (a[key as LogiFieldValue] !== b[key as LogiFieldValue]) {
      return false
    }
  }
  return true
}

/**
 * 画面側の形式チェック（buildPreview）の結果に、事前チェック API の結果を重ねる。
 *
 * - 事前チェックはその時点の修正値で走らせたものなので、チェック後に値を変えたセル（行）の
 *   問題は表示しない（直した直後に古いエラーが残らないように。次の事前チェックで改めて判定される）。
 * - 同じセルに画面側のエラーが既にあれば、サーバー側の問題は重ねない（画面側は入力のたびに
 *   判定し直されるので、そちらを優先する）。
 * - 表示している項目に紐づく問題はセルへ、項目を特定できない問題は行（rowErrors）へ、
 *   行にも紐づかない問題は globalIssues へ入れる。
 */
export const mergeServerCheck = (
  preview: PreviewResult,
  check: ServerCheck | null,
  currentOverrides: CellOverrides
): PreviewWithServerCheck => {
  if (!check) {
    return { ...preview, globalIssues: [], siteAutoCreates: [] }
  }
  const shownFields = new Set(preview.fields.map((f) => f.value))
  const byRow = new Map<number, ServerIssue[]>()
  const globalIssues: string[] = []
  for (const issue of check.issues) {
    if (issue.rowNo === null) {
      globalIssues.push(issue.message)
      continue
    }
    const list = byRow.get(issue.rowNo) ?? []
    list.push(issue)
    byRow.set(issue.rowNo, list)
  }

  const siteAutoCreates = check.siteAutoCreates.filter((s) =>
    isCellUnchanged(currentOverrides, check.checkedOverrides, s.rowNo, s.field)
  )
  // 画面側はマスタ候補に無いコードを「未登録」とするが、発着地は取り込み時に地点が作られる
  // （事前チェックの予告どおり）。予告のあるセルを未登録エラーのままにすると取り込めない。
  const autoCreatedCells = new Set(
    siteAutoCreates.map((s) => `${s.rowNo}:${s.field}`)
  )
  const withoutAutoCreated = (row: PreviewDataRow): PreviewDataRow => {
    const errors = row.errors.filter(
      (e) =>
        !(
          e.reason ===
            SHIPMENT_ORDER_CSV_IMPORT.PREVIEW_ERROR_REASON_MASTER_NOT_FOUND &&
          autoCreatedCells.has(`${row.rowNo}:${e.field}`)
        )
    )
    return errors.length === row.errors.length ? row : { ...row, errors }
  }

  const rows: PreviewDataRow[] = preview.rows.map((original) => {
    const row = withoutAutoCreated(original)
    const issues = byRow.get(row.rowNo)
    if (!issues) return row
    const errors = [...row.errors]
    const rowErrors = [...row.rowErrors]
    for (const issue of issues) {
      if (issue.field !== null && shownFields.has(issue.field)) {
        if (
          !isCellUnchanged(
            currentOverrides,
            check.checkedOverrides,
            row.rowNo,
            issue.field
          )
        ) {
          continue
        }
        if (errors.some((e) => e.field === issue.field)) continue
        errors.push({ field: issue.field, reason: issue.message })
        continue
      }
      if (
        !isRowUnchanged(currentOverrides, check.checkedOverrides, row.rowNo)
      ) {
        continue
      }
      rowErrors.push(issue.message)
    }
    return { ...row, errors, rowErrors }
  })

  const errorCount = rows.filter(isErrorRow).length
  return {
    ...preview,
    rows,
    okCount: rows.length - errorCount,
    errorCount,
    globalIssues,
    siteAutoCreates
  }
}

/** 地点を自動作成する項目と、その地点の名前を入れる項目の対応 */
const SITE_NAME_FIELD_OF: Record<SiteAutoCreate['field'], LogiFieldValue> = {
  originSiteId: 'originName',
  destSiteId: 'destName'
}

const SITE_NAME_FIELDS: ReadonlySet<LogiFieldValue> = new Set(
  Object.values(SITE_NAME_FIELD_OF)
)

/**
 * 確認画面に出す地点の名前の項目（発地名称・着地名称）。名称の列が無くても、次のときは表に出す:
 * - 事前チェックが、自動作成する地点に名前が無いことをその項目の問題として返した
 * - 確認画面でその項目に値を入れた（入れ終わって問題が消えても、入れた名前を見て直せるように）
 */
export const siteNameFieldsToShow = (
  issues: ReadonlyArray<ServerIssue>,
  overrides: CellOverrides
): Set<LogiFieldValue> => {
  const fields = new Set<LogiFieldValue>()
  for (const issue of issues) {
    if (issue.field !== null && SITE_NAME_FIELDS.has(issue.field)) {
      fields.add(issue.field)
    }
  }
  for (const cells of Object.values(overrides)) {
    for (const field of SITE_NAME_FIELDS) {
      if (cells?.[field] !== undefined) fields.add(field)
    }
  }
  return fields
}

/**
 * セルを直したときに同じ値を入れるセル。自動作成する地点の名前は地点（コード）ごとに 1 つなので、
 * 名前を入れた行と同じコードで地点を作る行すべて（発地・着地を問わない）に同じ名前を入れる。
 * それ以外のセルは直したセルだけ
 */
export const siteNameTargetsOf = (
  siteAutoCreates: ReadonlyArray<SiteAutoCreate>,
  rowNo: number,
  field: LogiFieldValue
): { rowNo: number; field: LogiFieldValue }[] => {
  const edited = siteAutoCreates.find(
    (s) => s.rowNo === rowNo && SITE_NAME_FIELD_OF[s.field] === field
  )
  if (!edited) return [{ rowNo, field }]
  const targets = siteAutoCreates
    .filter((s) => s.code === edited.code)
    .map((s) => ({ rowNo: s.rowNo, field: SITE_NAME_FIELD_OF[s.field] }))
  return targets
}
